import type { HolidayPublic } from '@repo/types/holidays';

import { useDeleteHoliday } from '@repo/api/holiday/useDeleteHoliday';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookTextIcon } from 'lucide-react';
import { useEffect, useMemo, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteLocation component.
 * @property {HolidayPublic[]} [holidays] - Array of holidays to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface DeleteHolidayDialogProps {
  holidays?: Array<HolidayPublic>;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function DeleteHolidayDialog(deleteHolidayDialogProps: DeleteHolidayDialogProps) {
  const { holidays, open, onOpenChange } = deleteHolidayDialogProps;
  const isSingle: boolean = holidays?.length === 1;

  const { mutateAsync, reset } = useDeleteHoliday();

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async () => {
    if (!holidays) return;
    await Promise.all(holidays.map((element) => mutateAsync(element.id))).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
  };

  const header = useMemo<string>(() => {
    if (isSingle) {
      return ' Usunąć Dzień Wolny ?';
    } else {
      return `Usunąć ${holidays?.length} Dni Wolne?`;
    }
  }, [isSingle, holidays]);

  const content = useMemo<ReactNode>(() => {
    if (isSingle) {
      return (
        <p className={'text-text-secondary'}>
          Czy na pewno chcesz usunąć dzień wolny{'  '}
          <b>{holidays?.[0]?.name}</b>?
        </p>
      );
    } else {
      return <p className={'text-text-secondary'}>Czy na pewno chcesz usunąć wybrane dni wolne ?</p>;
    }
  }, [isSingle, holidays]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <BookTextIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{header}</AlertDialogTitle>
              <AlertDialogDescription className={'text-text-tertiary text-xs'}>Usuń wybrane dni wolne.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto p-4'}>
            {content}
            {!isSingle && holidays && (
              <div className={'flex flex-col gap-3'}>
                <h5 className={'text-text-tertiary text-sm font-medium'}>Dni Wolne:</h5>
                <div className={'flex flex-wrap gap-2'}>
                  {holidays.map(({ name, id }) => {
                    return (
                      <Badge key={id} size={'sm'} variant={'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
